// Helpers for reading the currently logged-in user from localStorage.
// The login flow stores: localStorage.setItem('user', JSON.stringify(data.user))
// where data.user has at minimum { username, email, isAdmin, role }.

export const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

export const isCurrentUserAdmin = () => getCurrentUser()?.isAdmin === true;

// Usernames are stored as a single lowercase token (e.g. "spencerwright"), so
// there is no way to derive the forename/surname split from them. Certificates
// and other signed records must print the full name properly spaced, so the
// known accounts are mapped explicitly.
const FULL_NAMES = {
  adybates: 'Adrian Bates',
  adybrunning: 'Adrian Brunning',
  ethanbrunning: 'Ethan Brunning',
  gareth: 'Gareth Holton',
  gareth_holton: 'Gareth Holton',
  kevinwalker: 'Kevin Walker',
  office: 'Carol',
  pvsengineer: 'Dave',
  shartley: 'Stephen Hartley',
  spencerwright: 'Spencer Wright',
};

const titleCase = (value) => String(value || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .toLowerCase()
  .split(/[\s_.-]+/)
  .filter(Boolean)
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

// Best-effort display name. Prefers an explicit name/fullName field if the
// backend ever adds one, then the known-account map, otherwise title-cases the
// username (e.g. "gareth_holton" → "Gareth Holton").
export const getCurrentUserDisplayName = () => {
  const user = getCurrentUser();
  if (!user) return '';

  const clean = (value) => String(value || '').trim();
  const fullNameFromParts = `${clean(user.firstName)} ${clean(user.lastName)}`.trim();
  const mapped = FULL_NAMES[clean(user.username).toLowerCase()];

  if (clean(user.fullName)) return clean(user.fullName);
  if (fullNameFromParts) return fullNameFromParts;
  if (clean(user.displayName)) return clean(user.displayName);
  if (clean(user.name)) return clean(user.name);
  if (mapped) return mapped;
  if (clean(user.username)) return titleCase(user.username);
  if (clean(user.email)) return titleCase(clean(user.email).split('@')[0]);
  return '';
};

export const getDisplayNameForUsername = (username) => {
  const clean = String(username || '').trim().toLowerCase();
  if (!clean) return '';
  return FULL_NAMES[clean] || titleCase(clean);
};

export const getDisplayNameForUser = (user) => {
  if (!user) return '';
  if (typeof user === 'string') return getDisplayNameForUsername(user);
  const named = String(user.fullName || user.name || user.displayName || '').trim();
  if (named) return named;
  return getDisplayNameForUsername(user.username) || user.username || '';
};

export default getCurrentUserDisplayName;
